import { useEffect, useState } from 'react';
import { CaseAssetItem, InventoryAsset } from '../../../../types';
import { DetailViewCard } from '../../../Compounds/CardWithTitle';
import { ComponentSpinner } from '../../../Compounds/Loading/ComponentSpinner';
import { Drawer } from '../../../Atoms/Drawer';
import { DetailsDisplay } from '../../../Compounds/DetailsDrawer/DetailsDisplay';
import { Icon } from '../../../Atoms/Icon';
import { Text } from '../../../Atoms/Text';
import { fetchCaseAssets } from '../../../../Api/Cases/fetchCaseAssets';
import { getInventoryByIdSubject, getInventoryLoadingSubject, useInventory } from '../../../../store/Inventory';
import { useOutsideInventorySchemasStore } from '../../../../store/OutsideInventorySchema';
import { useProductsStore } from '../../../../store/Product';

const usaCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export interface CaseInventoryTableProps {
  caseId: string;
}

export function CaseInventoryTable({ caseId }: CaseInventoryTableProps) {
  const [assets, setAssets] = useState<CaseAssetItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<InventoryAsset | undefined>(undefined);
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [fetchAttempted, setFetchAttempted] = useState<boolean>(false);
  const { fetchInventoryById } = useInventory();
  const { fetchProducts } = useProductsStore();
  const { fetchOutsideInventorySchemas } = useOutsideInventorySchemasStore();

  useEffect(() => {
    fetchProducts();
    fetchOutsideInventorySchemas();
  }, []);

  useEffect(() => {
    if (!caseId) return;
    let active = true;
    setLoading(true);
    fetchCaseAssets(caseId)
      .then((res) => {
        if (active && res.data) setAssets(res.data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [caseId]);

  useEffect(() => {
    const sub = getInventoryByIdSubject().subscribe((asset) => {
      if (asset && asset.id === selectedAssetId) setSelectedAsset(asset);
    });
    const loadingSub = getInventoryLoadingSubject().subscribe((isLoading) => setInventoryLoading(isLoading));
    return () => {
      sub?.unsubscribe();
      loadingSub?.unsubscribe();
    };
  }, [selectedAssetId]);

  const openInventoryDrawer = (assetId: string) => {
    setSelectedAssetId(assetId);
    setSelectedAsset(undefined);
    setFetchAttempted(false);
    fetchInventoryById(assetId).finally(() => setFetchAttempted(true));
  };

  const closeInventoryDrawer = () => {
    setSelectedAssetId(null);
    setSelectedAsset(undefined);
    setFetchAttempted(false);
  };

  const drawerLoadFailed = !!selectedAssetId && fetchAttempted && !inventoryLoading && !selectedAsset;

  return (
    <>
      <DetailViewCard
        collapsed={collapsed}
        enableCollapse
        onCollapseClick={setCollapsed}
        childrenWrapperClass="overflow-x-auto"
        title={`Inventory (${assets.length})`}>
        <>
          {loading && (
            <div className="mt-4">
              <ComponentSpinner />
            </div>
          )}
          {!loading && assets.length === 0 && <div className="text-center">No Inventory</div>}
          {!loading && assets.length > 0 && (
            <table
              data-testid="case-inventory-table"
              className="w-full table-fixed [&_tr]:border-y">
              <thead>
                <tr className="text-indigo-mid text-xs text-left [&>*]:font-normal [&>*]:p-2">
                  <th className="border-r w-12"></th>
                  <th className="border-r">Address</th>
                  <th className="border-r w-32">Supplier</th>
                  <th className="border-r w-32">Product</th>
                  <th className="w-28">Monthly Cost</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="text-black text-sm text-left font-normal align-top [&>*]:p-2">
                    <td className="border-r">
                      <button
                        data-testid="case-inventory-detail-cta"
                        title="Open Inventory"
                        className="flex items-center justify-center w-full"
                        onClick={() => openInventoryDrawer(asset.id)}>
                        <Icon
                          type="details"
                          className="p-3 cursor-pointer"
                        />
                      </button>
                    </td>
                    <td
                      className="border-r break-words"
                      title={asset.address}>
                      {asset.address}
                    </td>
                    <td
                      className="border-r truncate"
                      title={asset.supplier?.name || ''}>
                      {asset.supplier?.name || ''}
                    </td>
                    <td
                      className="border-r truncate"
                      title={asset.product?.name || ''}>
                      {asset.product?.name || ''}
                    </td>
                    <td className="whitespace-nowrap">{usaCurrency.format(asset.mRC || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      </DetailViewCard>
      {selectedAssetId && (
        <Drawer
          data-testid="case-inventory-details-drawer"
          show={!!selectedAssetId}
          setShow={closeInventoryDrawer}
          title="Inventory Details"
          subtitle={selectedAsset && <Text size="lg">{selectedAsset.address || selectedAsset.id}</Text>}
          componentName="inventory"
          data={selectedAsset || null}>
          {selectedAsset && (
            <DetailsDisplay
              data={selectedAsset}
              componentName="inventory"
              setShow={closeInventoryDrawer}
            />
          )}
          {!selectedAsset && !drawerLoadFailed && (
            <div className="mt-10">
              <ComponentSpinner />
            </div>
          )}
          {drawerLoadFailed && (
            <div
              data-testid="case-inventory-details-error"
              className="mt-10 px-4 text-center">
              <Text size="sm">
                The inventory record could not be loaded. It may be hidden from view in the portal or unavailable for
                the currently selected account.
              </Text>
            </div>
          )}
        </Drawer>
      )}
    </>
  );
}
